import { trackAction } from '../../../../shared/utils/tracking/datadogRumTracking';

/**
 * Datadog RUM custom action emitted when one of the 526 "cause" free-text fields
 * reaches or exceeds its character limit. Used to measure demand for higher limits
 * (phase 2 of #142795), to inform future UX considerations.
 *
 * Only the length and field metadata are sent — never the veteran's text (PII).
 */
export const CAUSE_CHAR_LIMIT_ACTION = '21-526EZ_causeCharLimitReached';

/**
 * sessionStorage guard so we emit at most once per field per session rather than on
 * every validation pass while the user sits at the cap. If storage is unavailable
 * (private mode, blocked), we fall back to emitting each time rather than swallowing
 * the signal.
 *
 * @param {string} key - unique per-field storage key
 * @returns {boolean} true if this field has already been tracked this session
 */
const alreadyTracked = key => {
  try {
    if (sessionStorage.getItem(key) === 'true') return true;
    sessionStorage.setItem(key, 'true');
    return false;
  } catch (error) {
    return false;
  }
};

/**
 * Build a `ui:validations` entry that emits a RUM action the first time `value`
 * reaches or exceeds `limit`. It never adds a form error — the schema `maxLength`
 * owns validation; this only reports telemetry.
 *
 * @param {object} opts
 * @param {string} opts.field - form field key (e.g. 'primaryDescription')
 * @param {string} opts.cause - cause type: 'NEW' | 'SECONDARY' | 'WORSENED' | 'VA'
 * @param {number} opts.limit - the field's maxLength (kept in sync with the schema)
 * @returns {(errors: object, value: string) => void} a ui:validation function
 */
export const trackCauseCharLimit =
  ({ field, cause, limit }) =>
  (_errors, value) => {
    try {
      if (typeof value !== 'string' || value.length < limit) return;

      if (alreadyTracked(`${CAUSE_CHAR_LIMIT_ACTION}_${field}`)) return;

      trackAction(CAUSE_CHAR_LIMIT_ACTION, {
        formId: '21-526EZ',
        field,
        cause,
        limit,
        length: value.length,
        exceeded: value.length > limit,
      });
    } catch (error) {
      // Tracking must never break form validation.
    }
  };
